import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  usePreviewDataSourceMutation,
  useCreateDataSourceMutation,
  useUpdateDataSourceMutation,
} from "@/hooks/use-data-sources";
import { PageHeader } from "@/components/layout/page-header";
import { FormError } from "@/components/form-error";
import { SubmitButton } from "@/components/submit-button";
import { createDataSourceSnapshot } from "@/lib/api/data-sources";
import {
  createDataSourceSchema,
  type CreateDataSourceFormData,
} from "@/lib/validations/data-source";
import type { DataSource, DataSourcePreviewResponse } from "@/types/widget";
import { useServiceStore } from "@/stores/service-store";

interface DataSourceFormProps {
  mode: "create" | "edit";
  dataSource?: DataSource;
}

export function DataSourceForm({ mode, dataSource }: DataSourceFormProps) {
  const navigate = useNavigate();
  const { selectedServiceId } = useServiceStore();
  const previewMutation = usePreviewDataSourceMutation();
  const createMutation = useCreateDataSourceMutation();
  const updateMutation = useUpdateDataSourceMutation();
  const [previewResult, setPreviewResult] =
    useState<DataSourcePreviewResponse | null>(null);
  // Covers the snapshot upload that follows the save mutation as well.
  const [isSaving, setIsSaving] = useState(false);
  const [snapshotFailed, setSnapshotFailed] = useState(false);
  // Set once a create succeeds, so saving again (after a failed snapshot)
  // updates that data source instead of creating a duplicate.
  const [createdId, setCreatedId] = useState<string | null>(null);

  const headersToArray = (
    headers: Record<string, string> | undefined
  ): Array<{ key: string; value: string }> => {
    const entries = Object.entries(headers || {});
    return entries.length > 0
      ? entries.map(([key, value]) => ({ key, value }))
      : [{ key: "Authorization", value: "Bearer " }];
  };

  const defaultValues =
    mode === "edit" && dataSource
      ? {
          name: dataSource.name,
          url: (dataSource.definition?.url as string) || "",
          user_id: (dataSource.definition?.user_id as string) || "",
          query_param: (dataSource.definition?.query_param as boolean) ?? true,
          header: (dataSource.definition?.header as boolean) ?? true,
          include_headers: !!dataSource.definition?.headers,
          headers: headersToArray(
            dataSource.definition?.headers as Record<string, string>
          ),
        }
      : {
          name: "",
          url: "",
          user_id: "",
          query_param: true,
          header: true,
          include_headers: false,
          headers: [{ key: "Authorization", value: "Bearer " }],
        };

  const form = useForm<CreateDataSourceFormData>({
    resolver: zodResolver(createDataSourceSchema),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "headers",
  });

  useEffect(() => {
    if (mode === "edit" && dataSource) {
      form.reset({
        name: dataSource.name,
        url: (dataSource.definition?.url as string) || "",
        user_id: (dataSource.definition?.user_id as string) || "",
        query_param: (dataSource.definition?.query_param as boolean) ?? true,
        header: (dataSource.definition?.header as boolean) ?? true,
        include_headers: !!dataSource.definition?.headers,
        headers: headersToArray(
          dataSource.definition?.headers as Record<string, string>
        ),
      });
    }
  }, [dataSource, form, mode]);

  const userId = form.watch("user_id");
  const includeHeaders = form.watch("include_headers");
  const firstHeaderKeyRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!includeHeaders) {
      form.setValue("headers", [{ key: "Authorization", value: "Bearer " }]);
    } else {
      requestAnimationFrame(() => {
        firstHeaderKeyRef.current?.focus();
      });
    }
  }, [includeHeaders, form]);

  const clearApiError = () => {
    if (previewMutation.isError) {
      previewMutation.reset();
    }
  };

  const buildHeadersObject = (
    includeHeaders: boolean,
    headers: Array<{ key: string; value: string }>
  ): Record<string, string> | undefined => {
    if (!includeHeaders) return undefined;
    const obj = headers.reduce<Record<string, string>>((acc, { key, value }) => {
      if (key.trim()) acc[key.trim()] = value;
      return acc;
    }, {});
    return Object.keys(obj).length > 0 ? obj : undefined;
  };

  const onSubmit = async (data: CreateDataSourceFormData) => {
    previewMutation.reset();
    try {
      const headersObj = buildHeadersObject(data.include_headers, data.headers);
      const response = await previewMutation.mutateAsync({
        data: {
          name: data.name,
          url: data.url,
          user_id: data.user_id,
          query_param: data.query_param,
          header: data.header,
          ...(headersObj ? { headers: headersObj } : {}),
        },
        serviceId: selectedServiceId ?? undefined,
      });
      setPreviewResult(response.data);
    } catch {
      // Error is handled by mutation state
    }
  };

  const handleBack = () => {
    setPreviewResult(null);
    previewMutation.reset();
    createMutation.reset();
    updateMutation.reset();
    setSnapshotFailed(false);
  };

  const formatResponseBody = (body: string): string => {
    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      return body;
    }
  };

  const saveError = createMutation.error ?? updateMutation.error;

  const handleSave = async () => {
    createMutation.reset();
    updateMutation.reset();
    setSnapshotFailed(false);
    setIsSaving(true);
    const formData = form.getValues();
    const headersObj = buildHeadersObject(formData.include_headers, formData.headers);
    const payload = {
      name: formData.name,
      type: "url",
      definition: {
        url: formData.url,
        user_id: formData.user_id,
        query_param: formData.query_param,
        header: formData.header,
        ...(headersObj ? { headers: headersObj } : {}),
      },
    };

    try {
      const existingId = dataSource?.id ?? createdId;
      let dataSourceId: string;
      if (existingId) {
        await updateMutation.mutateAsync({ id: existingId, data: payload });
        dataSourceId = existingId;
      } else if (mode === "create") {
        const result = await createMutation.mutateAsync(payload);
        dataSourceId = result.data.id;
        setCreatedId(dataSourceId);
      } else {
        return;
      }

      if (previewResult?.body) {
        try {
          await createDataSourceSnapshot(dataSourceId, {
            user_id: formData.user_id,
            snapshot: previewResult.body,
          });
        } catch {
          setSnapshotFailed(true);
          return;
        }
      }

      navigate(`/widgets/data-sources/${dataSourceId}`);
    } catch {
      // Error is handled by mutation state
    } finally {
      setIsSaving(false);
    }
  };

  const isCreate = mode === "create";
  const pageTitle = isCreate ? "Create Data Source" : "Edit Data Source";
  const saveButtonText = isCreate ? "Create data source" : "Save changes";
  const savingButtonText = isCreate ? "Creating..." : "Saving...";
  const canSave =
    !!previewResult &&
    previewResult.valid &&
    previewResult.code >= 200 &&
    previewResult.code < 300;

  // The keys stop React from reusing the Back button as Preview: the click on
  // Back would then submit the form it brings back and preview straight away.
  const headerActions = previewResult ? (
    <>
      <Button
        key="back"
        type="button"
        variant="outline"
        size="sm"
        onClick={handleBack}
        disabled={isSaving}
      >
        Back
      </Button>
      {canSave && (
        <SubmitButton
          key="save"
          type="button"
          onClick={handleSave}
          pending={isSaving}
          pendingText={savingButtonText}
        >
          {saveButtonText}
        </SubmitButton>
      )}
    </>
  ) : (
    <SubmitButton
      key="preview"
      form="data-source-form"
      pending={form.formState.isSubmitting}
      pendingText="Previewing..."
    >
      Preview
    </SubmitButton>
  );

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: "Widgets", to: "/widgets" },
          { label: "Data Sources", to: "/widgets/data-sources" },
          ...(dataSource
            ? [{ label: dataSource.name, to: `/widgets/data-sources/${dataSource.id}` }]
            : []),
        ]}
        title={pageTitle}
        actions={headerActions}
      />

      {previewResult ? (
        <Card className="mx-auto w-full max-w-3xl">
          <CardHeader>
            <CardTitle>Preview Response</CardTitle>
            <CardDescription>
              Review and approve the response from your data source.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground">HTTP Code</Label>
                <p className="font-mono text-lg">{previewResult.code}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground">Content Type</Label>
                <p className="font-mono text-lg">{previewResult.type}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Response Body</Label>
              <pre className="rounded-md bg-muted p-4 overflow-auto max-h-80 text-[13px] font-mono whitespace-pre-wrap break-words">
                {formatResponseBody(previewResult.body)}
              </pre>
            </div>

            {previewResult.code < 200 || previewResult.code >= 300 ? (
              <FormError>
                The data source returned HTTP {previewResult.code}.<br />
                A successful response (2xx) is required to create a data source.
              </FormError>
            ) : !previewResult.valid ? (
              <FormError>
                The response is not valid. Please check your data source
                configuration and try again.
              </FormError>
            ) : null}

            {snapshotFailed && (
              <FormError>
                The data source was saved, but its preview response could not
                be stored. Please try again.
              </FormError>
            )}

            {saveError && (
              <FormError>
                <p>
                  {saveError instanceof AxiosError &&
                  saveError.response?.data?.error?.message
                    ? saveError.response.data.error.message
                    : `Failed to ${isCreate ? "create" : "update"} data source. Please try again.`}
                </p>
                <p className="mt-1">
                  Name must be unique.
                </p>
              </FormError>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="mx-auto w-full max-w-3xl">
          <CardHeader>
            <CardTitle>Data Source Details</CardTitle>
            <CardDescription>
              Configure your data source connection.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              id="data-source-form"
              onSubmit={form.handleSubmit(onSubmit)}
              onChange={clearApiError}
              className="space-y-6"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="name">
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <span className="text-[13px] text-muted-foreground">
                    Must be unique
                  </span>
                </div>
                <Input
                  id="name"
                  autoFocus={isCreate}
                  placeholder="My Data Source"
                  maxLength={255}
                  {...form.register("name")}
                  aria-invalid={!!form.formState.errors.name}
                  aria-describedby={
                    form.formState.errors.name ? "name-error" : undefined
                  }
                />
                {form.formState.errors.name && (
                  <p
                    id="name-error"
                    className="text-[13px] text-destructive"
                    role="alert"
                  >
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="url">
                  URL <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="url"
                  type="url"
                  placeholder="https://api.example.com/data"
                  {...form.register("url")}
                  aria-invalid={!!form.formState.errors.url}
                  aria-describedby={
                    form.formState.errors.url ? "url-error" : undefined
                  }
                />
                {form.formState.errors.url && (
                  <p
                    id="url-error"
                    className="text-[13px] text-destructive"
                    role="alert"
                  >
                    {form.formState.errors.url.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="user_id">
                    User ID <span className="text-destructive">*</span>
                  </Label>
                  <span className="text-[13px] text-muted-foreground">
                    To preview the data
                  </span>
                </div>
                <Input
                  id="user_id"
                  placeholder="user123"
                  {...form.register("user_id")}
                  aria-invalid={!!form.formState.errors.user_id}
                  aria-describedby={
                    form.formState.errors.user_id ? "user_id-error" : undefined
                  }
                />
                {form.formState.errors.user_id && (
                  <p
                    id="user_id-error"
                    className="text-[13px] text-destructive"
                    role="alert"
                  >
                    {form.formState.errors.user_id.message}
                  </p>
                )}
              </div>

              <fieldset>
                <legend className="text-[13px] font-medium leading-none">
                  How to send User ID <span className="text-destructive">*</span>
                </legend>
                <p className="mt-2 text-[13px] text-muted-foreground">
                  User ID will be set to the ID you used to link your user with
                  Appio.{" "}
                  <a
                    href="https://docs.appio.so/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                  >
                    Documentation
                  </a>
                </p>
                <div className="mt-3 space-y-3">
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      className="size-4 cursor-pointer rounded border-input"
                      {...form.register("query_param")}
                    />
                    <span className="text-[13px]">
                      <span className="font-medium">Query String Param</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        ?user_id={userId || "..."}
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      className="size-4 cursor-pointer rounded border-input"
                      {...form.register("header")}
                    />
                    <span className="text-[13px]">
                      <span className="font-medium">HTTP Header</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        X-User-Id: {userId || "..."}
                      </span>
                    </span>
                  </label>
                </div>
                {form.formState.errors.query_param && (
                  <p className="mt-2 text-[13px] text-destructive" role="alert">
                    {form.formState.errors.query_param.message}
                  </p>
                )}
              </fieldset>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  className="size-4 cursor-pointer rounded border-input"
                  {...form.register("include_headers")}
                />
                <span className="text-[13px] font-medium">Extra Headers</span>
              </label>

              {includeHeaders && (
                <fieldset className="space-y-3 rounded-lg bg-muted/50 p-4">
                  <legend className="sr-only">Headers</legend>
                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end"
                      >
                        <div className="space-y-2">
                          {index === 0 && <Label>Key</Label>}
                          {(() => {
                            const { ref: registerRef, ...registerRest } =
                              form.register(`headers.${index}.key`);
                            return (
                              <Input
                                placeholder="Name"
                                ref={(el) => {
                                  registerRef(el);
                                  if (index === 0)
                                    firstHeaderKeyRef.current = el;
                                }}
                                {...registerRest}
                              />
                            );
                          })()}
                        </div>
                        <div className="space-y-2">
                          {index === 0 && <Label>Value</Label>}
                          <Input
                            placeholder="Value"
                            {...form.register(`headers.${index}.value`)}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="cursor-pointer text-muted-foreground hover:text-destructive"
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                          aria-label={`Remove header ${index + 1}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="cursor-pointer"
                    onClick={() => append({ key: "", value: "" })}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add header
                  </Button>
                </fieldset>
              )}

              {previewMutation.isError && (
                <FormError>
                  <p>
                    {previewMutation.error instanceof AxiosError &&
                    previewMutation.error.response?.data?.error?.message
                      ? previewMutation.error.response.data.error.message
                      : "Failed to preview data source. Please try again."}
                  </p>
                </FormError>
              )}
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
